import { authProvider } from '../auth/authProvider';
import { createMockDataProvider } from './mockDataProvider';
import { createSeedData } from './seed';
import { withAudit } from './withAudit';

/**
 * THE SWAP SEAM.
 *
 * The entire app talks to this one `dataProvider`. Today it is the in-memory
 * mock (Path 1 — see DECISIONS.md). To go live, replace the mock below with the
 * real provider pointed at the admin API, e.g.:
 *
 *   import simpleRestProvider from 'ra-data-simple-rest';
 *   const base = simpleRestProvider(import.meta.env.VITE_ADMIN_API_URL);
 *
 * Nothing else in the app changes: surfaces only ever call the DataProvider
 * interface, never the mock dataset.
 *
 * THE AUDIT FLOOR (D-audit). The provider is wrapped by `withAudit` so EVERY
 * mutation through it is recorded automatically — no surface can skip the trail.
 * This is composed here, at the seam, so it survives the swap: keep it wrapping
 * the real provider for interim client-side auditing, or drop it once the admin
 * API audits server-side (the real enforcement point). The audit READ path
 * (`getList('audit')`) is a normal provider call either way.
 */
const mockProvider = createMockDataProvider(createSeedData());

export const dataProvider = withAudit(mockProvider, {
  authProvider,
  sink: mockProvider,
});

// Entity types are the consolidated target schema (D-schema-target) — surfaces
// import these for typing.
export type {
  Market,
  RuleMarket,
  Currency,
  Merchant,
  MerchantCategory,
  MerchantStatus,
  MerchantRegion,
  StoreChannel,
  ChannelType,
  TrackingMethod,
  CashbackFundingSource,
  StoreTag,
  MerchantRegionTag,
  AffiliateNetwork,
  AffiliateNetworkMarket,
  User,
  UserTier,
  SignupMethod,
  Transaction,
  TransactionStatus,
  AttributionMethod,
  CashoutRequest,
  CashoutDestination,
  PayoutMethod,
  CashoutStatus,
  Charity,
  CharityType,
  CharityStatus,
  Category,
  CategoryStatus,
  Click,
  CannedResponse,
  CannedResponseKind,
  CannedMacroAction,
  ImpersonationSession,
  DsarRequest,
  DsarStatus,
  ConsentRecord,
  ConsentType,
  ConsentAction,
  ConsentSource,
  SanctionsMatch,
  SanctionsList,
  SanctionsStatus,
  ErasureRequest,
  ErasureStatus,
  RateRule,
  RuleScopeType,
  RuleEffectType,
  RuleStatus,
  RuleScopeRef,
  PageSchema,
  PageSchemaStatus,
  PublishedVersion,
  BlockWindow,
  BlockSchedules,
  CmsPage,
  CmsPageKind,
  CmsPageMarket,
  CmsPageStatus,
  BlogPost,
  BlogCategory,
  Testimonial,
  TestimonialStatus,
  SeoRule,
  SliderSlide,
  SliderCta,
  MenuItem,
  MenuLink,
  MenuLinkKind,
  OfflineStore,
  OfflineStoreStatus,
  CardLinkConfig,
  SystemSetting,
  FeatureFlag,
  PlusMarketingFlag,
  PlusMarketingState,
  EmailTemplate,
  ComplianceThreshold,
  ComplianceStateToggle,
  ComplianceLegalReference,
  Event,
  EventType,
  AlertType,
  AlertMarket,
  AlertConfig,
  AlertChannelConfig,
  FiredAlert,
  TipTapDoc,
  TipTapNode,
  TipTapMark,
  Dataset,
  DashboardStats,
  AdminDataProvider,
  AuditAction,
  AuditLogEntry,
  AuditEntryDraft,
  AuditSink,
  StoreDetail,
  StoreDetailMarket,
  StoreDetailChannel,
  StoreDetailTag,
} from './types';

export {
  CURRENCY_BY_MARKET,
  SEED_MARKETS,
  REDACTED_MARKER,
  SENSITIVE_AUDIT_FIELDS,
  CONSENT_TYPES,
  SANCTIONS_LISTS,
  SANCTIONS_STATUSES,
  ERASED_MARKER,
  CHANNEL_TYPE_LABELS,
  CHANNEL_TYPE_VALUES,
  EVENT_TYPES,
  ALERT_TYPES,
} from './types';
